// src/components/store/sections/product-cards/DoorHangerProductCard.tsx
"use client";

import Link from "next/link";
import Image from "next/image";
import { ShoppingCart, Zap, Check, Loader2 } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const HANGER_THEMES = [
  { gradient: "from-[#00b050] to-[#007a37]", badge: "Special Pick" },
  { gradient: "from-[#0072ce] to-[#004a87]", badge: "Trending" },
  { gradient: "from-[#f59e0b] to-[#d97706]", badge: "Hot Deal" },
  { gradient: "from-[#e11d48] to-[#9f1239]", badge: "Best Seller" },
  { gradient: "from-[#0284c7] to-[#075985]", badge: "Verified" },
  { gradient: "from-[#7c3aed] to-[#5b21b6]", badge: "Top Rated" },
];

export default function DoorHangerProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    description,
    currentPrice,
    added,
    buying,
    handleAddToCart,
    handleBuyNow,
  } = useCardActions(product, subdomain);

  const theme = HANGER_THEMES[index % HANGER_THEMES.length];

  return (
    <div className="w-full h-full group relative flex flex-col justify-between bg-white rounded-[38px] border-2 border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden select-none min-h-[430px]">
      {/* 1. Door Hanger Top Cutout */}
      <div className="relative w-full pt-4 pb-2 flex flex-col items-center">
        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-300 font-mono">
          DOOR HANGER
        </span>
        <div className="relative w-full h-16 flex items-center justify-center mt-1">
          <div className="absolute left-0 w-[55%] h-4 bg-slate-100/90 rounded-r-full shadow-[inset_0_2px_4px_rgba(0,0,0,0.12)] border-y border-slate-200/50" />
          <div className="w-16 h-16 rounded-full bg-slate-100/90 shadow-[inset_0_4px_8px_rgba(0,0,0,0.18)] border border-slate-200/60 flex items-center justify-center relative z-10">
            <div className="w-full h-full rounded-full bg-gradient-to-b from-slate-200/40 via-transparent to-white/80" />
          </div>
        </div>
      </div>

      {/* 2. Diagonal Gradient Slice */}
      <div
        className={`absolute inset-x-0 bottom-0 h-[63%] bg-gradient-to-b ${theme.gradient} transition-transform duration-300 group-hover:scale-[1.02]`}
        style={{ clipPath: "polygon(0% 28%, 100% 0%, 100% 100%, 0% 100%)" }}
      />

      {/* 3. Floating Product Image */}
      <div className="relative z-10 px-4 -mt-3 mb-2 flex flex-col items-center">
        <Link href={productUrl} className="block w-full cursor-pointer">
          <div className="relative w-full h-36 flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-2">
            <div className="relative w-32 h-32">
              <Image
                src={productImg}
                alt={productName}
                fill
                unoptimized
                className="object-contain drop-shadow-[0_16px_20px_rgba(0,0,0,0.35)]"
              />
            </div>
          </div>
        </Link>
        <div className="w-24 h-2.5 rounded-[100%] bg-black/30 blur-xs -mt-2 pointer-events-none" />
      </div>

      {/* 4. Bottom Typography & Actions */}
      <div className="relative z-10 px-5 pb-5 pt-1 text-center text-white flex flex-col justify-end space-y-2.5">
        <div className="space-y-0.5">
          <span className="text-xs font-serif italic tracking-wide text-white/90 block">
            {theme.badge}
          </span>
          <Link href={productUrl} className="block cursor-pointer">
            <h3 className="text-base sm:text-lg font-black uppercase tracking-tight text-white truncate drop-shadow-sm">
              {productName}
            </h3>
          </Link>
          <p className="text-[10px] text-white/80 line-clamp-1 leading-snug px-1">
            {description}
          </p>
        </div>

        <div className="py-0.5">
          <span className="text-2xl font-black tracking-tight text-white drop-shadow-md font-mono">
            ৳{Math.round(currentPrice).toLocaleString()}
          </span>
        </div>

        <div className="space-y-1.5 pt-1">
          <button
            type="button"
            onClick={(e) => handleAddToCart(e)}
            className="w-full py-2.5 px-3 rounded-full bg-white text-slate-900 font-black text-[11px] uppercase tracking-wider shadow-md hover:bg-slate-100 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5"
          >
            {added ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-emerald-700">ADDED</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-3.5 h-3.5 text-slate-900" />
                <span>ADD TO CART</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={(e) => handleBuyNow(e)}
            className="w-full py-2 px-3 rounded-full bg-black/40 hover:bg-black/60 text-white font-black text-[10px] uppercase tracking-wider backdrop-blur-xs transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1"
          >
            {buying ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Zap className="w-3 h-3 fill-current text-amber-300" />
            )}
            <span>{buying ? "WAIT..." : "BUY NOW"}</span>
          </button>
        </div>

        <span className="text-[8px] font-mono tracking-widest text-white/60 uppercase block pt-0.5">
          DO NOT DISTURB
        </span>
      </div>
    </div>
  );
}
